import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DataGrid, GridToolbarExport, GridToolbarQuickFilter } from '@mui/x-data-grid';

import AdminPage, { AdminCard, SectionHeader } from './components/admin/AdminPage';
import { StatTile, DailyChart, DailyChartAxis, CampusBars } from './components/admin/Stats';
import { colour, dataGridSx, pillSx } from './components/admin/tokens';
import ReviewChangesDialog from './components/approval/ReviewChangesDialog';
import { SyncPills } from './components/approval/FamilySummary';
import { fetchApprovals, fetchApprovalStats, fetchEnrolledStudents } from './api/approvals';
import { adminApi } from './api/clients';
import { changeSummary, durationLabel } from './utils/approvalSummary';
import { accountsColumns } from './components/admin/accountsColumns';
import { holdingColumn, journeyColumn } from './components/admin/journeyColumns';
import { latestCommentColumn } from './components/admin/commentColumns';
import { contactColumn, entries, familyColumn, frozenColumnSx, studentColumn, useFrozenFirstColumn } from './components/admin/familyColumns';
import DateRangeButton from './components/admin/DateRangeButton';

/** Every filter the list understands, all unset. */
const BLANK_FILTERS = {
    paidFrom: '', paidTo: '', settledFrom: '', settledTo: '',
    receipted: '', hcc: '', cardSent: '', staff: '', discount: '', settled: '',
};

const activeFilterCount = (filters) =>
    Object.values(filters).filter((value) => String(value ?? '').trim() !== '').length;
import {
    getAdminCampuses,
    getAdminName,
    getAdminTeam,
    getAdminToken,
    isAdminSignedIn,
    mayApprove,
    saveAdminSession,
} from './utils/adminSession';
import { fetchAdminSession } from './api/staffAccess';
import { formatMoney } from './utils/money';
import { searchByFamily, searchByStudent } from './utils/familySearch';
import { withCampusSections } from './utils/campusSections';

/**
 * What the campuses a staff member looks after have been doing, and what still needs them.
 *
 * This was a single unstyled table of families who had changed something. It could not say how
 * many that was, could not be narrowed to one campus by somebody who looks after several, and
 * had no way to show a family who re-enrolled without changing anything — so the queue was the
 * only view, and there was nowhere to look up a family who was not in it.
 *
 * Two teams read it, and not for the same reasons. The enrolment team works the approval queue:
 * what a family changed, and whether to accept it. The accounts team is not in that queue at all
 * — they are looking at the Health Care Card and the money — so the queue's views, its columns
 * and its counts are simply not drawn for them. The server leaves the other team's fields out of
 * the response as well, so this is which sections exist, not a curtain over them.
 */

/**
 * Every view either team can be in. Each count above the list is one of these, so a tile is
 * something you can press rather than a number you read and then go looking for.
 *
 * `needs` keeps a view to the team it means something to, and the first one that survives is
 * where that team opens: the approval queue for enrolment, who has paid for accounts.
 */
const ALL_VIEWS = [
    {
        key: 'waiting',
        label: 'Waiting for approval',
        needs: 'changes',
        query: { scope: 'waiting', status: 'pending' },
        empty: 'Nothing is waiting. Everything that needed a decision has had one.',
    },
    {
        key: 'approved',
        label: 'Approved',
        needs: 'changes',
        query: { scope: 'waiting', status: 'approved' },
        empty: 'Nothing has been approved yet.',
    },
    {
        key: 'paid',
        label: 'Paid',
        needs: 'amounts',
        query: { scope: 'all', stage: 'paid' },
        empty: 'Nobody has finished and paid yet.',
    },
    {
        key: 'today',
        // Every family who opened the form today, finished or not, which "started" says and
        // "came in" did not.
        label: 'Started today',
        query: { scope: 'all', stage: 'today' },
        empty: 'Nobody has started today.',
    },
    {
        key: 'inProgress',
        label: 'Total in progress',
        query: { scope: 'all', stage: 'in-progress' },
        empty: 'Nobody is part-way through.',
    },
    {
        /**
         * Families whose card the bank refused — and NOTHING WAS TAKEN from any of them.
         *
         * They were inside "Total in progress", which is true but useless: a family who has not
         * finished needs a reminder, while these need telling why their payment failed. On
         * production the eight reasons were insufficient funds, a card not yet activated,
         * cardholder not authenticated, fraud screening and a lost session — four of which a family
         * fixes in a minute once somebody says so.
         *
         * The amounts on these rows are what was ATTEMPTED. Every money column shows "Declined"
         * instead, because AR reading a refused attempt as a part-payment is the thing to prevent.
         */
        key: 'rejected',
        label: 'Payment declined',
        query: { scope: 'all', stage: 'rejected' },
        empty: 'No payments have been declined.',
    },
    {
        key: 'all',
        // Finished ones only. Counting families still part-way through made the total read as
        // more re-enrolments than there are; they have their own count, Total in progress.
        label: 'All re-enrolments',
        query: { scope: 'all', stage: 'paid' },
        empty: 'Nobody has finished re-enrolling yet for your campuses.',
    },
    {
        /**
         * A row per child rather than per family: the children the roll by child counts as
         * Enrolled on Not re-enrolled — on a finished re-enrolment, and not marked as leaving.
         *
         * Read from its own endpoint, so the family filters (paid, settled and the headings) do
         * not apply here and are not drawn. A row does not open the family: the dialog needs the
         * family's whole record, which this list does not carry.
         */
        key: 'enrolledStudents',
        label: 'Enrolled students',
        students: true,
        empty: 'No children have been re-enrolled yet for your campuses.',
    },
];

/** Kindy, PP, Year 1 … Year 12 in school order rather than as text, where Year 10 sorts before Year 2. */
const yearGroupRank = (label) => {
    const text = String(label || '').trim();
    if (/^kindy$/i.test(text)) return -1;
    if (/^pp$/i.test(text)) return 0;
    const year = text.match(/(\d{1,2})$/);
    return year ? Number(year[1]) : 99;
};

/** "30 Oct 2026": the day the family finished. A blank for one from before that was recorded. */
const finishedDay = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * What to say under the elapsed figure.
 *
 * Small samples get told on themselves. A median of two is arithmetic, not a finding, and a tile
 * that looked equally confident either way is how the old mean came to read as "1d 22h typical".
 */
const elapsedHint = (timing) => {
    if (!timing?.measured) return 'none finished yet';

    const { measured, inOneSitting } = timing;
    if (measured < 5) {
        return `only ${measured} finished so far`;
    }

    // How many did it without leaving it overnight — the part of the spread that is mostly form
    // rather than mostly sleep.
    return `median of ${measured} · ${inOneSitting} in one sitting`;
};

const ApproverScreen = () => {
    const navigate = useNavigate();
    const signedIn = isAdminSignedIn();

    // What this person's job is, and therefore which sections this screen has at all.
    //
    // Held in state and asked of the server on every visit, not read once from the sign-in.
    // Whoever hands out access changes somebody's team and then watches them reload the page —
    // if that showed the old screen until they signed out and in again, the setting would look
    // as though it had not worked.
    const [team, setTeam] = useState(() => getAdminTeam());
    const showsChanges = team !== 'accounts';
    const showsAmounts = team !== 'enrolment';
    const showsCard = team !== 'enrolment';
    const views = useMemo(() => ALL_VIEWS.filter((entry) => {
        if (entry.needs === 'changes') return showsChanges;
        if (entry.needs === 'amounts') return showsAmounts;
        return true;
    }), [showsChanges, showsAmounts]);
    const viewIndex = useCallback(
        (key) => Math.max(0, views.findIndex((entry) => entry.key === key)),
        [views]
    );

    const [view, setView] = useState(0);
    const [campus, setCampus] = useState('');
    const [rows, setRows] = useState([]);
    // Holds the frozen Family heading over its column as the grid scrolls sideways.
    const gridApiRef = useFrozenFirstColumn();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [reviewing, setReviewing] = useState(null);
    const [approving, setApproving] = useState(false);
    const [approveError, setApproveError] = useState('');
    // The accounts list is fifteen columns wide if every field gets its own. Compact combines
    // the pairs that said the same thing twice and hides the three that are reference rather
    // than reading; the full set is one button away for an export.
    const [allColumns, setAllColumns] = useState(false);
    const [filters, setFilters] = useState(BLANK_FILTERS);
    // Like the campuses: read only from an unfiltered load, or narrowing to one discount would
    // leave the list offering just that one and no way back.
    const [discountTypes, setDiscountTypes] = useState([]);

    const campuses = getAdminCampuses();
    // Somebody who holds every campus has no list of their own, so theirs is learned from the
    // counts. Remembered, not read from the current counts: those are now scoped to whatever the
    // filter says, so reading them live would leave the dropdown holding only the campus already
    // chosen — and no way back to the others.
    const [knownCampuses, setKnownCampuses] = useState([]);
    const campusChoices = useMemo(
        () => (campuses === 'all' ? knownCampuses : (Array.isArray(campuses) ? campuses : [])),
        [campuses, knownCampuses]
    );
    // What the dropdown offers: Kewdale as Kewdale Primary and Kewdale Secondary, which the server
    // narrows by the children's year groups. Somebody who holds only Kewdale can now filter too.
    const filterChoices = useMemo(() => withCampusSections(campusChoices), [campusChoices]);
    const canFilterByCampus = filterChoices.length > 1;

    // The team is fetched after the first render, so the set of views can change under a
    // selection that was valid a moment ago. Falling back to the first is how each team ends up
    // opening where it should: the approval queue, or who has paid.
    const current = views[view] || views[0];

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError('');

        try {
            const [list, counts] = await Promise.all([
                current.students
                    ? fetchEnrolledStudents({ college: campus })
                    : fetchApprovals({ ...current.query, college: campus, ...filters }),
                // The same campus: counts covering every campus above a list showing one reads
                // as a fault, and there is no way to tell which is answering the filter.
                fetchApprovalStats({ college: campus }),
            ]);
            setRows(list);
            setStats(counts);

            // Only while nothing is filtered, for the reason above.
            if (!campus) {
                setKnownCampuses((counts?.byCampus || []).map((entry) => entry.campus));
                setDiscountTypes(counts?.discountTypes || []);
            }
        } catch (error) {
            // An empty table and a failed request look identical otherwise, and one of them means
            // families are waiting with nobody seeing them.
            setLoadError(
                error?.response?.status === 401
                    ? 'Your sign-in has expired. Please sign in again.'
                    : 'Could not load the list. Please try again.'
            );
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [current, campus, filters]);

    useEffect(() => {
        if (!signedIn) {
            navigate('/admin-login', { replace: true });
            return undefined;
        }

        let ignore = false;
        // Failing here is not worth stopping the screen for: the stored answer is what they had
        // last time, and every endpoint decides again for itself regardless.
        fetchAdminSession()
            .then((session) => {
                if (ignore) return;
                saveAdminSession({ token: getAdminToken(), ...session });
                setTeam(session.team || 'both');
            })
            .catch(() => {});

        return () => { ignore = true; };
    }, [signedIn, navigate]);

    useEffect(() => {
        if (signedIn) load();
    }, [signedIn, load]);

    const approveRecord = async (row) => {
        setApproving(true);
        setApproveError('');

        try {
            await adminApi.post('/approveParentRecord', {
                SessionID: row.id,
                approved_by: getAdminName(),
            });
            await load();
            setReviewing(null);
        } catch (error) {
            setApproveError(error?.response?.data?.error || 'Could not approve this record.');
        } finally {
            setApproving(false);
        }
    };

    // x-data-grid is 6.20 here, where valueGetter and renderCell each take a single params
    // object. The (value, row) form belongs to v7 and fails at runtime rather than at build:
    // row arrives undefined, and the column throws the moment it reads a field.
    //
    // The search box looks at the family name and the parent code only (searchByFamily). Left to
    // itself it searched every column, so a parent code turned up other families by their phone
    // numbers, receipts and amounts — a different family on the Paid tab, read as the answer.
    const columns = useMemo(() => searchByFamily(showsChanges ? [
        // The same first three as every staff table: the family (code under it, frozen), the
        // campus, and both parents' contact details (familyColumns.js).
        familyColumn({ name: (row) => row.family_name, code: (row) => row.parent_id }),
        ...(canFilterByCampus ? [{ field: 'college', headerName: 'Campus', width: 115 }] : []),
        contactColumn({
            phones: (row) => entries(row.mobile1, row.mobile2),
            emails: (row) => entries(row.e_mail, row.e_mail2),
        }),
        // Where the parent got to and what is holding them, before anything about approving: on
        // the in-progress view the question is which of 465 families to ring, and "What changed"
        // says "Nothing to approve" for nearly all of them.
        journeyColumn(),
        holdingColumn(),
        ...(showsChanges ? [{
            field: 'changed',
            headerName: 'What changed',
            flex: 1.4,
            minWidth: 200,
            sortable: false,
            valueGetter: (params) => (params.row.needs_approval ? changeSummary(params.row) : 'Nothing to approve'),
            renderCell: (params) => (
                <Typography sx={{
                    fontSize: '13.5px',
                    color: params.row.needs_approval ? colour.body : colour.faint,
                }}>
                    {params.value}
                </Typography>
            ),
        }] : []),
        ...(showsCard ? [{
            field: 'hcc',
            headerName: 'Health care card',
            width: 155,
            valueGetter: (params) => (
                String(params.row.health_care_card || '').toLowerCase() === 'yes'
                    ? (params.row.hcc_expiry_date || 'Claimed')
                    : ''
            ),
            renderCell: (params) => {
                if (!params.value) {
                    return <Typography sx={{ fontSize: '13.5px', color: colour.faint }}>Not claimed</Typography>;
                }
                // A claim with no picture is the one an AR officer has to chase, so it is the
                // one the column points at.
                const sent = Number(params.row.hcc_image_count || 0) > 0;
                return (
                    <Chip
                        size="small"
                        label={sent ? params.value : `${params.value} · no card sent`}
                        sx={pillSx(sent ? 'info' : 'warn')}
                    />
                );
            },
        }] : []),
        ...(showsAmounts ? [{
            field: 'paid',
            headerName: 'Paid',
            width: 110,
            valueGetter: (params) => (params.row.payment?.paid ? params.row.payment.paidAmount : null),
            renderCell: (params) => (
                <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: params.value ? colour.ink : colour.faint }}>
                    {params.value == null ? 'Unpaid' : formatMoney(params.value)}
                </Typography>
            ),
        },
        {
            field: 'credit',
            headerName: 'Credit used',
            width: 110,
            valueGetter: (params) => params.row.payment?.fromCredit || 0,
            renderCell: (params) => (
                <Typography sx={{ fontSize: '13.5px', color: params.value ? colour.body : colour.faint }}>
                    {params.value ? formatMoney(params.value) : '—'}
                </Typography>
            ),
        }] : []),
        {
            field: 'took',
            headerName: 'Took',
            width: 95,
            valueGetter: (params) => params.row.timing?.minutesToComplete ?? null,
            renderCell: (params) => (
                <Typography sx={{ fontSize: '13.5px', color: params.value == null ? colour.faint : colour.body }}>
                    {/* Blank rather than a dash of zero: a family from before this was recorded
                        has no duration, which is not the same as having taken no time. */}
                    {params.value == null ? '—' : durationLabel(params.value)}
                </Typography>
            ),
        },
        {
            field: 'tass',
            headerName: 'TASS',
            width: showsChanges ? 230 : 150,
            sortable: false,
            filterable: false,
            // Both steps matter to whoever approves; only the receipt matters to accounts, and
            // the approval pill would be a status they cannot act on.
            renderCell: (params) => <SyncPills sync={params.row.sync || {}} showDetails={showsChanges} />,
        },
        // The row opens the family (onRowClick), so there is no button column to aim at.
        latestCommentColumn(),
    ] : accountsColumns({
        showCampus: canFilterByCampus,
        compact: !allColumns,
        filters,
        onFilter: setFilters,
        discountTypes,
    })),
    [canFilterByCampus, showsChanges, showsAmounts, showsCard, allColumns, filters, discountTypes]);

    // The Enrolled students tab: the child first (ID under the name, frozen), then the family, as
    // every list of students has it. The first and last names, the student ID and the parent code
    // are their own columns too, hidden on screen and in the export, as the accounts view does.
    const studentColumns = useMemo(() => searchByStudent([
        studentColumn({ name: (row) => [row.firstName, row.lastName].filter(Boolean).join(' '), code: (row) => row.studentId }),
        { field: 'studentId', headerName: 'Student ID', width: 110 },
        { field: 'firstName', headerName: 'First name', width: 130 },
        { field: 'lastName', headerName: 'Last name', width: 130 },
        {
            field: 'yearGroup',
            headerName: 'Next year',
            width: 110,
            sortComparator: (a, b) => yearGroupRank(a) - yearGroupRank(b),
        },
        ...(canFilterByCampus ? [{ field: 'campus', headerName: 'Campus', width: 115 }] : []),
        familyColumn({ field: 'familyName', name: (row) => row.familyName, code: (row) => row.parentCode, frozen: false }),
        { field: 'parentCode', headerName: 'Parent code', width: 110 },
        {
            field: 'completedAt',
            headerName: 'Family finished',
            width: 140,
            // Sorted by the stored time; shown, and exported, as the day.
            valueFormatter: (params) => finishedDay(params.value),
            renderCell: (params) => (
                <Typography sx={{ fontSize: '13.5px', color: params.value ? colour.body : colour.faint }}>
                    {finishedDay(params.value) || '—'}
                </Typography>
            ),
        },
    ]), [canFilterByCampus]);

    const openFamily = (row) => { setApproveError(''); setReviewing(row); };

    // A comment added in the dialog shows on the row straight away, without reloading the list.
    const commentsChanged = (rowId, comments) => {
        setRows((current) => current.map((row) => (row.id === rowId ? { ...row, comments } : row)));
        setReviewing((current) => (current && current.id === rowId ? { ...current, comments } : current));
    };

    const scopeLabel = campus
        || (campuses === 'all' ? 'every campus' : campusChoices.join(', ') || 'no campus yet');

    return (
        <AdminPage
            wide
            title="Re-enrolments"
            lede={showsChanges
                ? `What families have sent in for ${scopeLabel}, and what still needs a decision.`
                : `What families have sent in for ${scopeLabel}, what they were charged and what they paid.`}
            actions={canFilterByCampus && (
                <TextField
                    select
                    size="small"
                    label="Campus"
                    value={campus}
                    onChange={(event) => setCampus(event.target.value)}
                    sx={{ minWidth: 190, bgcolor: colour.surface, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                >
                    <MenuItem value="">All my campuses</MenuItem>
                    {filterChoices.map((name) => (
                        <MenuItem key={name} value={name}>{name}</MenuItem>
                    ))}
                </TextField>
            )}
        >
            {loadError && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{loadError}</Alert>
            )}

            {campusChoices.length === 0 && campuses !== 'all' && (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px' }}>
                    You are signed in, but no campus has been assigned to you yet, so there is
                    nothing to show. Whoever looks after access can add one.
                </Alert>
            )}

            <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap', mb: 2 }}>
                {showsChanges && (
                    <StatTile
                        label="Waiting for approval"
                        value={stats?.waiting ?? '—'}
                        tone="warn"
                        onClick={() => setView(viewIndex('waiting'))}
                        active={current.key === 'waiting'}
                    />
                )}
                {showsChanges && (
                    <StatTile
                        label="Approved"
                        value={stats?.approved ?? '—'}
                        tone="good"
                        onClick={() => setView(viewIndex('approved'))}
                        active={current.key === 'approved'}
                    />
                )}
                {showsAmounts && (
                    <StatTile
                        label="Paid"
                        value={stats?.paid ?? '—'}
                        tone="good"
                        hint={stats ? `${stats.receipted} receipted in TASS` : undefined}
                        onClick={() => setView(viewIndex('paid'))}
                        active={current.key === 'paid'}
                    />
                )}
                {/* The same families counted once each, however many campuses they hold.
                    Beside Paid rather than instead of it: a parent code is per campus by design, so
                    a family at two campuses is rightly two paid enrolments and one family. On
                    production that is 325 against 309 — sixteen families at more than one campus.
                    Not clickable: there is no "one row per family" list to open, and a tile that
                    did nothing when pressed would be worse than one that plainly does not. */}
                {showsAmounts && (
                    <StatTile
                        label="Families paid"
                        value={stats?.paidFamilies ?? '—'}
                        hint="counted once across campuses"
                    />
                )}
                <StatTile
                    label="Started today"
                    value={stats?.today ?? '—'}
                    tone="info"
                    onClick={() => setView(viewIndex('today'))}
                    active={current.key === 'today'}
                />
                <StatTile
                    label="Total in progress"
                    value={stats?.inProgress ?? '—'}
                    hint="Started, not finished"
                    onClick={() => setView(viewIndex('inProgress'))}
                    active={current.key === 'inProgress'}
                />
                {/* Counted apart from "in progress": these families tried to pay and the bank said
                    no. The hint spells out that no money arrived, because the amounts on those rows
                    are what the family attempted rather than anything we hold.

                    Worded without "was": the accounts view must never render a parent's previous
                    value, and AdminTeams guards that by refusing /was\s/ anywhere on the screen. */}
                <StatTile
                    label="Payment declined"
                    value={stats?.rejected ?? '—'}
                    hint="No money taken"
                    tone="warn"
                    onClick={() => setView(viewIndex('rejected'))}
                    active={current.key === 'rejected'}
                />
                <StatTile
                    label="Start to finish"
                    value={stats?.timing?.medianMinutes == null
                        ? '—'
                        : durationLabel(stats.timing.medianMinutes)}
                    // The middle of them, and how many it was taken from. Two things this tile
                    // deliberately does not claim: that it is "typical" when it was measured over
                    // a handful, and that it is time spent — a family who starts in the evening
                    // and finishes next morning counts the night in between, because nothing
                    // records when they were actually at the screen.
                    hint={elapsedHint(stats?.timing)}
                />
                <StatTile
                    label="All re-enrolments"
                    value={stats?.paid ?? '—'}
                    hint="Finished, not counting in progress"
                    onClick={() => setView(viewIndex('all'))}
                    active={current.key === 'all'}
                />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <AdminCard sx={{ flex: '2 1 380px', minWidth: 0 }}>
                    <SectionHeader title="The last fortnight" />
                    <Box sx={{ p: 2 }}>
                        <DailyChart days={stats?.byDay || []} />
                        <DailyChartAxis days={stats?.byDay || []} />
                    </Box>
                </AdminCard>

                {canFilterByCampus && (
                    <AdminCard sx={{ flex: '1 1 280px', minWidth: 0 }}>
                        <SectionHeader title="By campus" />
                        <Box sx={{ p: 2 }}>
                            {/* The shaded part of each bar is what is still waiting for a
                                decision. That is not a queue accounts works, so they get the
                                totals alone rather than a bar implying something of theirs is
                                outstanding. */}
                            <CampusBars
                                campuses={(stats?.byCampus || []).map((entry) => (
                                    showsChanges ? entry : { ...entry, pending: 0 }
                                ))}
                            />
                        </Box>
                    </AdminCard>
                )}
            </Box>

            <AdminCard>
                <Box sx={{ borderBottom: `1px solid ${colour.border}`, px: { xs: 1, sm: 2 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Tabs
                        value={views.indexOf(current)}
                        onChange={(event, value) => setView(value)}
                        sx={{
                            minHeight: 46,
                            '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: '13.5px', minHeight: 46, color: colour.muted },
                            '& .Mui-selected': { color: `${colour.primaryDeep} !important` },
                            '& .MuiTabs-indicator': { bgcolor: colour.primary, height: 3, borderRadius: '3px 3px 0 0' },
                        }}
                    >
                        {views.map((entry) => <Tab key={entry.key} label={entry.label} />)}
                    </Tabs>
                    <Box sx={{ pr: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        {!current.students && activeFilterCount(filters) > 0 && (
                            <Button
                                size="small"
                                onClick={() => setFilters(BLANK_FILTERS)}
                                sx={{ textTransform: 'none', fontWeight: 700, color: colour.primaryDeep }}
                            >
                                Clear {activeFilterCount(filters)} filters
                            </Button>
                        )}
                        {/* Children on the Enrolled students tab, families everywhere else: a row is one of either. */}
                        <Chip size="small" label={current.students ? `${rows.length} students` : `${rows.length} shown`} sx={pillSx('neutral')} />
                    </Box>
                </Box>


                <Box sx={{ p: { xs: 1, sm: 2 } }}>
                    <DataGrid
                        apiRef={gridApiRef}
                        // Every column rendered, so the frozen first one is never dropped as the grid
                        // scrolls sideways; a page holds at most 50 rows.
                        disableVirtualization
                        autoHeight
                        loading={loading}
                        rows={rows}
                        columns={current.students ? studentColumns : columns}
                        rowHeight={52}
                        disableRowSelectionOnClick
                        disableColumnMenu
                        /**
                         * Hidden on screen, still exported.
                         *
                         * The compact view shows the parent code under the family name and the
                         * mobile and email in one Contact column, which is what AR asked for and
                         * saves about 330px of width. But this grid's CSV goes into Power BI, where
                         * a name and a code in one cell is useless — so these three stay defined
                         * and the export is told to include every column, hidden ones too.
                         */
                        columnVisibilityModel={allColumns ? { comments: false } : {
                            parent_id: false,
                            mobile1: false,
                            mobile2: false,
                            e_mail: false,
                            e_mail2: false,
                            // Every comment in one cell is for the export; the screen shows the
                            // latest, and the family's dialog shows them all.
                            comments: false,
                        }}
                        /**
                         * The row opens the family: the card images, the fee breakdown, the changes
                         * and the comments. It replaced a View/Review button column, which cost
                         * width on every row to say what a click on the row now does. The quick
                         * filter and the column filters live in the toolbar and the headings, not
                         * in rows, so a click there never opens anything; Enter on a focused cell
                         * opens the row too.
                         */
                        // A child's names, ID and parent code have their own columns for the export,
                        // hidden on screen, where the Student and Family columns already show them.
                        {...(current.students ? { columnVisibilityModel: {
                            studentId: false, firstName: false, lastName: false, parentCode: false,
                        } } : {})}
                        onRowClick={current.students ? undefined : (params) => {
                            // A drag to select text ends in a click on the row. AR copy receipt
                            // numbers into TASS and addresses into mail from these cells, so a
                            // selection must not open the family.
                            if (window.getSelection()?.toString()) return;
                            openFamily(params.row);
                        }}
                        onCellKeyDown={(params, event) => {
                            if (event.key === 'Enter' && !current.students) openFamily(params.row);
                        }}
                        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                        pageSizeOptions={[10, 25, 50]}
                        slots={{
                            toolbar: () => (
                                <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <GridToolbarQuickFilter
                                        placeholder={current.students ? 'Search a student, family or code' : 'Search a family or parent code'}
                                        sx={{ width: { xs: '100%', sm: 260 }, '& .MuiInput-root': { fontSize: '14px' } }}
                                    />
                                    {/* The rest of the filters live on the column headings they
                                        belong to. Dates have no heading of their own to sit on —
                                        a date range is not one cell's value — so they stay here,
                                        one button each rather than a "from" and a "to". */}
                                    {!current.students && (
                                    <DateRangeButton
                                        name="Paid"
                                        from={filters.paidFrom}
                                        to={filters.paidTo}
                                        hint="When the family finished and paid."
                                        onChange={({ from, to }) => setFilters((current) => ({
                                            ...current, paidFrom: from, paidTo: to,
                                        }))}
                                    />
                                    )}
                                    {showsAmounts && !current.students && (
                                        <DateRangeButton
                                            name="Settled"
                                            from={filters.settledFrom}
                                            to={filters.settledTo}
                                            hint="When BPoint settled the money, which is a different day from when the family paid."
                                            onChange={({ from, to }) => setFilters((current) => ({
                                                ...current, settledFrom: from, settledTo: to,
                                            }))}
                                        />
                                    )}
                                    {/* The accounts view is read against BPoint's own reports and
                                        lifted into Power BI, so it can be taken away as a file
                                        rather than copied off the screen. */}
                                    {!showsChanges && !current.students && (
                                        <Button
                                            size="small"
                                            onClick={() => setAllColumns((shown) => !shown)}
                                            sx={{ ml: 'auto', textTransform: 'none', fontWeight: 700, color: colour.primaryDeep }}
                                        >
                                            {allColumns ? 'Compact' : 'All columns'}
                                        </Button>
                                    )}
                                    {/* The students go out as a file for either team: a list of
                                        names is what somebody takes away to check against a class list. */}
                                    {(!showsChanges || current.students) && (
                                        <GridToolbarExport
                                            // allColumns so the hidden parent code, mobile and
                                            // email are in the CSV even though the compact view
                                            // folds them into two columns on screen.
                                            csvOptions={{
                                                allColumns: true,
                                                fileName: `${current.students ? 'enrolled-students' : 're-enrolments'}-${new Date().toISOString().slice(0, 10)}`,
                                            }}
                                            printOptions={{ disableToolbarButton: true }}
                                            sx={{ textTransform: 'none', fontWeight: 700, color: colour.primaryDeep }}
                                        />
                                    )}
                                </Box>
                            ),
                            noRowsOverlay: () => (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', px: 3 }}>
                                    <Typography sx={{ fontSize: '14px', color: colour.faint, textAlign: 'center' }}>
                                        {current.empty}
                                    </Typography>
                                </Box>
                            ),
                        }}
                        // The row is what you click, so it says so — except a child's, which opens nothing.
                        sx={{ ...dataGridSx, ...frozenColumnSx, '& .MuiDataGrid-row': { cursor: current.students ? 'default' : 'pointer' } }}
                    />
                </Box>
            </AdminCard>

            <ReviewChangesDialog
                row={reviewing}
                open={Boolean(reviewing)}
                approving={approving}
                error={approveError}
                onClose={() => setReviewing(null)}
                onApprove={approveRecord}
                onCommentsChange={commentsChanged}
                // The server says per row: a signed manual entry may be approved by either team.
                canApprove={reviewing?.may_approve ?? mayApprove()}
            />
        </AdminPage>
    );
};

export default ApproverScreen;
